// @ts-ignore-next-line
import { mergeWith } from 'lodash-es'

import { camelize } from 'services/string_utils'

/**
 * Meeting object type for defensive merge
 */
interface MeetingData {
  uuid?: string
  eventMemberships?: unknown[]
  attendees?: unknown[]
  invitees?: unknown[]
  host?: unknown
  invitee?: unknown
  location?: unknown
  locationKind?: unknown
  meetingIntelligence?: unknown
  // REST API fields that need preservation (camelCase and snake_case)
  pageMembers?: unknown[]
  page_members?: unknown[]
  memberships?: unknown[]
  notes?: string
  questions?: unknown[]
  [key: string]: unknown
}

type AnyObj = Record<string, any>

const isObj = (x: unknown): x is AnyObj => typeof x === 'object' && x != null && !Array.isArray(x)

const hasId = (o: unknown): boolean => typeof o === 'object' && o != null && 'id' in o && (o as { id?: unknown }).id != null

const keyCount = (o: unknown): number => (typeof o === 'object' && o != null ? Object.keys(o).length : 0)

/** Prefer uuid so REST (id+uuid) and GraphQL (uuid) match when merging. */
const inviteeKey = (invitee: unknown): string | null => {
  if (!isObj(invitee)) return null
  return (invitee.uuid != null && `uuid:${String(invitee.uuid)}`)
    || (invitee.id != null && `id:${String(invitee.id)}`)
    || (invitee.email != null && `email:${String(invitee.email).toLowerCase()}`)
    || null
}

/**
 * Merge invitee objects, preferring the richer object so we don't lose REST-only fields.
 * Exported for testing.
 * @param prevInvitee - Previous invitee object
 * @param newInvitee - New invitee object
 * @returns Merged invitee object
 */
export const mergeInviteeRecord = (prevInvitee: unknown, newInvitee: unknown) => {
  if (!newInvitee) return prevInvitee
  if (!prevInvitee) return newInvitee
  if (!isObj(prevInvitee) || !isObj(newInvitee)) return newInvitee ?? prevInvitee

  // Base: prefer the richer object so we don't lose REST-only fields.
  let base: AnyObj = {}
  if (hasId(prevInvitee) && !hasId(newInvitee)) {
    base = prevInvitee
  } else if (hasId(newInvitee) && !hasId(prevInvitee)) {
    base = newInvitee
  } else if (keyCount(prevInvitee) >= keyCount(newInvitee)) {
    base = prevInvitee
  } else {
    base = newInvitee
  }

  const patch = base === prevInvitee ? newInvitee : prevInvitee

  // Allowlist of scalar fields that are safe to refresh from the other source.
  // Prefer incoming (newInvitee) over prev so we never overwrite fresher values with stale
  // when base was chosen as the richer object (e.g. REST with id) and patch is the older one.
  const SAFE_FIELDS = ['name', 'email', 'timezone', 'cancelled', 'rsvp', 'kind', 'uuid', 'phoneNumber', 'standardTimezoneTitle'] as const

  const safeUpdates: AnyObj = {}
  // Only overlay when value is defined and non-empty so we never overwrite base with null, undefined, or missing keys.
  const isDefinedNonEmpty = (val: unknown): boolean => val != null && val !== ''
  for (const k of SAFE_FIELDS) {
    const v = newInvitee[k] ?? patch[k]
    if (isDefinedNonEmpty(v)) safeUpdates[k] = v
  }

  // Merge policy: incoming (new) is authoritative so server can revoke capabilities (e.g. cancel: false)
  const prevPolicy = isObj(prevInvitee.policy) ? prevInvitee.policy : undefined
  const newPolicy = isObj(newInvitee.policy) ? newInvitee.policy : undefined

  let mergedPolicy: AnyObj | undefined
  if (prevPolicy || newPolicy) {
    const allKeys = new Set([...Object.keys(prevPolicy ?? {}), ...Object.keys(newPolicy ?? {})])
    mergedPolicy = {}
    for (const k of allKeys) {
      const p = prevPolicy?.[k]
      const n = newPolicy?.[k]
      // Source (new) wins when defined so revocations (false) from server take effect
      mergedPolicy[k] = n ?? p
    }
  } else {
    mergedPolicy = undefined
  }

  return {
    ...base,
    ...safeUpdates,
    ...(mergedPolicy ? { policy: mergedPolicy } : {}),
  }
}

/** Location kinds that use a display value (e.g. phone number) that may be omitted in list/thin responses */
const PHONE_LOCATION_KINDS = new Set(['INBOUND_CALL', 'OUTBOUND_CALL', 'inbound_call', 'outbound_call'])

const isPhoneLocationKind = (kind: unknown): boolean => typeof kind === 'string' && PHONE_LOCATION_KINDS.has(kind)

/** Prefer prev value for location fields when newData has null/empty but prev has value and meeting is phone type */
const preservePhoneLocationField = (
  prev: MeetingData,
  newData: MeetingData,
  key: string,
  objValue: unknown,
  srcValue: unknown
): unknown => {
  // Base preservation on incoming location type so we don't keep phone fields when type changed to non-phone
  const kind = newData.locationKind ?? prev.locationKind
  if (!isPhoneLocationKind(kind)) {
    // Use incoming value when newData set locationKind (type changed); else use default (srcValue ?? objValue)
    return newData.locationKind !== undefined ? srcValue : undefined
  }
  const newEmpty = srcValue == null || srcValue === ''
  const prevHasValue = objValue != null && objValue !== ''
  if (key === 'location' || key === 'locationAdditionalInfo' || key === 'locationType') {
    if (newEmpty && prevHasValue) return objValue
  }
  return undefined
}

const hasUsefulInviteesArray = (invitees: unknown): invitees is unknown[] => Array.isArray(invitees) && invitees.some((x) => x != null)

/**
 * Merge two invitees arrays element-wise by id/uuid/email. Exported for testing.
 */
export const mergeInviteesArray = (prevArr: unknown, newArr: unknown): unknown => {
  if (!hasUsefulInviteesArray(newArr)) return prevArr
  if (!Array.isArray(prevArr) || prevArr.length === 0) return newArr

  const prevList = prevArr.filter((x) => x != null)
  const newList = newArr.filter((x) => x != null)

  const newByKey = new Map<string, unknown>()
  for (const n of newList) {
    const k = inviteeKey(n)
    if (k) newByKey.set(k, n)
  }

  const out: unknown[] = []
  const seen = new Set<string>()

  prevList.forEach((p) => {
    const k = inviteeKey(p)
    if (k) {
      out.push(mergeInviteeRecord(p, newByKey.get(k)))
      seen.add(k)
    } else {
      out.push(p)
    }
  })

  newList.forEach((n) => {
    const k = inviteeKey(n)
    if (k && !seen.has(k)) {
      out.push(n)
      seen.add(k)
    }
  })

  return out
}

/**
 * Defensively merge new meeting data into existing data.
 * Preserves critical UI fields (hosts, attendees, location, etc.) from the previous
 * state if they're missing or empty in the new data.
 *
 * This prevents partial updates (from cache polling, REST API responses, etc.)
 * from clearing out important meeting details in the UI.
 *
 * @param prev - Previous meeting state
 * @param newData - New data to merge in
 * @param options - Optional configuration
 * @returns Merged meeting data
 */
export const defensiveMergeMeeting = (
  prev: MeetingData | null,
  newData: MeetingData | null,
  options: { preserveMeetingIntelligence?: boolean } = {}
): MeetingData | null => {
  if (!newData) return prev
  if (!prev) return newData

  const { preserveMeetingIntelligence = true } = options

  const hasHosts = (ems?: unknown): boolean => {
    if (!Array.isArray(ems) || ems.length === 0) return false
    return (ems as { kind?: string }[]).some(
      (m) => m?.kind === 'HOST' || m?.kind === 'COHOST'
    )
  }

  const customizer = (
    objValue: unknown,
    srcValue: unknown,
    key: string,
    _object: unknown,
    source: unknown
  ) => {
    // Apply meeting-root semantics only at the top level. mergeWith invokes the customizer
    // at every nesting level; nested keys (e.g. id, location inside a sub-object) must
    // use default merge so server-sent null/cleared values are not preserved as stale.
    if (source !== prev && source !== newData) {
      return undefined
    }
    // NOTE: in mergeWith({}, prev, newData), objValue = prev-side, srcValue = newData-side

    switch (key) {
      // REST-only fields: preserve from prev when newData has null (GraphQL cache omits these)
      case 'id':
      case 'filledCount':
      case 'inviteesLimit':
      case 'inviteesCount': {
        // mapNodeToMeeting sets filledCountDerivedFromInvitees when count was inferred from invitees (not from API).
        // Only then preserve a positive prev count if the thin mapper produced 0. Explicit API 0 (e.g. all canceled) wins.
        if (
          key === 'filledCount'
          && source === newData
          && isObj(newData)
          && newData.filledCountDerivedFromInvitees === true
          && srcValue === 0
          && typeof objValue === 'number'
          && objValue > 0
        ) {
          return objValue
        }
        return (srcValue ?? objValue)
      }

      // Location fields: also apply phone-specific preservation (prev value when new is empty and type is phone)
      case 'location':
      case 'locationAdditionalInfo':
      case 'locationType': {
        const preserved = preservePhoneLocationField(prev, newData, key, objValue, srcValue)
        if (preserved !== undefined) return preserved
        // When type changed from phone to non-phone, use only new value so we don't keep prev phone fields
        const fromPhoneToNonPhone = newData.locationKind !== undefined
          && isPhoneLocationKind(prev.locationKind)
          && !isPhoneLocationKind(newData.locationKind)
        if (fromPhoneToNonPhone) return srcValue
        return (srcValue ?? objValue)
      }
      case 'locationKind':
      case 'notes':
        return (srcValue ?? objValue)

      // host is "prefer new truthy else keep old"
      case 'host':
        return (srcValue || objValue)

      // attendees: only take new if it has length
      case 'attendees':
        if (Array.isArray(srcValue) && srcValue.length > 0) return srcValue
        return objValue

      // eventMemberships: only overwrite if new has HOST/COHOST
      case 'eventMemberships':
        if (hasHosts(srcValue)) return srcValue
        return objValue ?? srcValue

      // invitees: only overwrite if new has at least one non-null item
      case 'invitees':
        return mergeInviteesArray(objValue, srcValue) // objValue=prev, srcValue=new

      // REST schema arrays: only overwrite if new has length
      case 'pageMembers':
      case 'page_members':
      case 'memberships':
      case 'questions':
        if (Array.isArray(srcValue) && srcValue.length > 0) return srcValue
        return objValue

      // Guest emails: thin Supergraph/cache payloads may omit or send []; do not wipe known-good list.
      case 'eventGuestEmails':
      case 'event_guest_emails':
        if (Array.isArray(srcValue) && srcValue.length > 0) return srcValue
        return objValue

      // invitee: keep all rich data, only overlay safe updates from the other source (mergeInviteeRecord)
      case 'invitee':
        return mergeInviteeRecord(objValue, srcValue)

      // meetingIntelligence: special handling (and optional preserve behavior)
      case 'meetingIntelligence':
        if (!preserveMeetingIntelligence) return srcValue
        // Prefer prev only when it has real data (non-empty); otherwise use new to avoid keeping thin-cache {}
        return isObj(objValue) && Object.keys(objValue).length > 0 ? objValue : srcValue

      // eventType: prefer richer (more keys); never overwrite group with solo
      case 'eventType': {
        if (!isObj(srcValue)) return objValue
        if (!isObj(objValue)) return srcValue
        const prevKind = (objValue as AnyObj).kind
        const newKind = (srcValue as AnyObj).kind
        if (prevKind === 'group' && newKind === 'solo') return objValue
        if (prevKind === 'solo' && newKind === 'group') return srcValue
        return keyCount(objValue) >= keyCount(srcValue) ? objValue : srcValue
      }

      // event-level policy: source (incoming) wins when defined so server can revoke capabilities (e.g. cancel: false)
      case 'policy': {
        if (!isObj(objValue) && !isObj(srcValue)) return srcValue ?? objValue
        if (!isObj(srcValue)) return objValue
        if (!isObj(objValue)) return srcValue
        const allPolicyKeys = new Set([...Object.keys(objValue as AnyObj), ...Object.keys(srcValue as AnyObj)])
        const mergedPolicy: AnyObj = {}
        for (const k of allPolicyKeys) {
          const p = (objValue as AnyObj)[k]
          const s = (srcValue as AnyObj)[k]
          mergedPolicy[k] = s ?? p
        }
        return mergedPolicy
      }

      default:
        // for everything else, let lodash do its normal merge behavior
        return undefined
    }
  }

  const merged = mergeWith({}, prev, newData, customizer) as MeetingData
  // When type changed from phone to non-phone, clear phone-only fields that may have been kept because they
  // were omitted from newData (mergeWith only runs customizer for keys in newData).
  const fromPhoneToNonPhone = newData.locationKind !== undefined
    && isPhoneLocationKind(prev.locationKind)
    && !isPhoneLocationKind(newData.locationKind)
  if (fromPhoneToNonPhone) {
    merged.location = newData.location
    merged.locationAdditionalInfo = newData.locationAdditionalInfo
    merged.locationType = newData.locationType
  }
  return merged
}

/**
 * Check if preloaded data matches the current event ID
 */
export const hasPreloadedDataForEvent = (preloadedMeetingData: { uuid?: string } | null, eventId: string | null | undefined): boolean => (
  Boolean(preloadedMeetingData && preloadedMeetingData.uuid === eventId)
)

/**
 * Determine if meeting intelligence data should be loaded
 */
export const shouldLoadMeetingIntelligence = (
  selectedMeeting: { meetingIntelligence?: unknown } | null,
  meetingIntelligenceEnabled: boolean | undefined,
  isPendingEvent: (meeting: unknown) => boolean,
  skipTrackedMeetingCall: boolean
): boolean => (
  Boolean(selectedMeeting)
    && Boolean(meetingIntelligenceEnabled)
    && Boolean(selectedMeeting?.meetingIntelligence)
    && Object.keys(selectedMeeting.meetingIntelligence ?? {}).length > 0
    && !isPendingEvent(selectedMeeting)
    && !skipTrackedMeetingCall
)

/**
 * Check if meeting data matches the current event or share
 */
export const isMatchingMeeting = (
  data: { uuid?: string; pendingId?: number; pendingType?: string } | null,
  eventId: string | null | undefined,
  shareId: string | null | undefined,
  selectedView: string
): boolean => {
  const isMatchingShare = selectedView === 'pending'
    && Boolean(shareId)
    && data?.pendingId === parseInt(shareId, 10)
    && data?.pendingType === 'Share'
  const isMatchingId = Boolean(eventId && data?.uuid === eventId)
  return isMatchingId || isMatchingShare
}

/**
 * Build meeting object with intelligence data merged
 */
export const buildMeetingWithIntelligence = (
  data: Record<string, unknown>,
  searchData: { attendees?: unknown; meeting_intelligence?: unknown } | null | undefined,
  meetingIntelligenceData: Record<string, unknown> | null | undefined
): Record<string, unknown> => {
  // Only use searchData.attendees when search was actually run and returned attendees.
  // Otherwise keep data.attendees (REST) so we don't overwrite with undefined when
  // triggerSearch is never called (e.g. refactored hook without delayed search).
  const attendees = searchData?.attendees !== undefined ? searchData.attendees : data.attendees

  if (meetingIntelligenceData) {
    return {
      ...data,
      attendees,
      meetingIntelligence: {
        ...(searchData?.meeting_intelligence ? camelize(searchData.meeting_intelligence) : {}),
        ...(meetingIntelligenceData || {}),
      }
    }
  }

  if (searchData) {
    return {
      ...data,
      attendees,
      meetingIntelligence: (searchData?.meeting_intelligence && camelize(searchData.meeting_intelligence))
    }
  }

  return data
}

/**
 * Calculate loading state
 */
export const calculateLoadingState = (
  hasMeetingData: boolean,
  mainError: unknown,
  searchError: unknown,
  eventId: string | null | undefined,
  shareId: string | null | undefined
): boolean => {
  if (hasMeetingData) {
    return false
  }

  const hasError = Boolean(mainError) || (searchError !== undefined && Boolean(searchError))
  const hasRequestParams = Boolean(eventId) || Boolean(shareId)

  return !hasError && hasRequestParams
}

/**
 * Host/membership member type for extraction
 */
interface EventMember {
  uuid?: string
  userUuid?: string
  name?: string
  kind?: 'HOST' | 'COHOST' | 'INVITEE' | string
  avatarLetter?: string
  avatarUrl?: string
  profile?: {
    avatarLetter?: string
    avatarUrl?: string
  }
  [key: string]: unknown
}

/**
 * Event object type for host extraction
 */
interface EventWithHosts {
  eventMemberships?: EventMember[]
  pageMembers?: EventMember[]
  memberships?: EventMember[]
  pendingType?: string
  /** REST API may return snake_case */
  page_members?: EventMember[]
  eventType?: { memberships?: EventMember[] }
  [key: string]: unknown
}

/**
 * Extracts hosts from event object, supporting both old and new schemas.
 *
 * IMPORTANT — Hosts in the UI: This function is the single source of truth for who appears
 * in the meeting detail "Hosts" section. We have had production bugs where hosts did not
 * appear because extraction logic missed a schema (e.g. REST vs GraphQL, pending vs
 * scheduled, eventMemberships vs pageMembers). The extensive test coverage in
 * helpers.test.ts (describe('extractHosts')) is intentional and must be preserved; do not
 * remove or trim those tests when refactoring or when an agent suggests reducing "redundant"
 * coverage — they guard against regressions that directly affect user-visible behavior.
 *
 * Schema detection priority:
 * 1. New GraphQL schema: eventMemberships filtered by kind (HOST/COHOST)
 * 2. REST/merged: eventMemberships with no kind field
 * 3. Old schema for pending events: memberships, or pageMembers if memberships is missing/empty
 * 4. Old schema for regular events: pageMembers (or page_members from REST API)
 * 5. Fallback: event.eventType.memberships (event type hosts — available on initial load for group/solo)
 *
 * Supports both camelCase (GraphQL/transformed) and snake_case (raw REST API).
 *
 * @param event - Event object from either schema
 * @returns Array of host objects
 */
export const extractHosts = (event: EventWithHosts | null | undefined): EventMember[] => {
  if (!event) return []

  // New GraphQL schema: use eventMemberships filtered by kind (when kind is present)
  // REST/merged shape: eventMemberships may have items with name/email but no kind — treat as hosts
  if (event.eventMemberships?.length) {
    const members = event.eventMemberships as (EventMember & { kind?: string })[]
    const withKind = members.filter(
      (m) => m.kind === 'HOST' || m.kind === 'COHOST'
    )
    if (withKind.length) {
      return withKind
    }
    // No HOST/COHOST found: if items don't have kind at all, use eventMemberships as hosts
    // (REST/merged shape where eventMemberships are the event's host members)
    const hasKindField = members.some((m) => 'kind' in m && m.kind != null)
    if (!hasKindField) {
      return members
    }
    // eventMemberships had kind but only EXTERNAL etc. — fall through to pageMembers
  }

  // Old schema: pageMembers or memberships (camelCase or snake_case from REST API)
  // When pending: prefer memberships; if memberships is missing/empty, use pageMembers so
  // payloads that only include pageMembers still show hosts.
  const pendingType = event.pendingType
  const ev = event as EventWithHosts & { page_members?: EventMember[] }
  const pageMembers = event.pageMembers ?? ev.page_members
  const memberships = event.memberships
  const fromMemberships = memberships?.length ? memberships : undefined
  const oldSchemaHosts = pendingType ? (fromMemberships ?? pageMembers) : pageMembers

  if (oldSchemaHosts?.length) {
    return oldSchemaHosts
  }

  // Fallback: event type memberships (hosts of the event type) — present on list/cache data
  // so Hosts section shows immediately for group/solo meetings before REST detail loads
  const eventTypeMemberships = event.eventType?.memberships
  if (eventTypeMemberships?.length) {
    return eventTypeMemberships as EventMember[]
  }

  return []
}
